import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsToolsOptions, getOrgVendorIntegrationsOptions } from '@onefootprint/fetch-dashboard';
import type { AgentTool } from '@onefootprint/request-types/dashboard';
import { Card, InlineAlert, Select, Shimmer, Toggle } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toolSummary, VendorLogo } from '@/modules/agent';
import { enabledToolKeys, graphToolForCatalogKind, isToolAvailable, toolKey, toolKeyForCatalogKind } from '@/utils/vendor-kind';
import useBuilderNavigation from '../../hooks/use-builder-navigation';
import { useBuilder } from '../../store/provider';
import ToolFilters, { activeToolFilters } from '../tool-filters';
import { findOrphanedToolMentions } from './utils/orphaned-tool-mentions';
import { integrationOptionsForKind } from './utils/tool-integrations';
const ToolsView = () => {
  const $ = _c(31);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.tools"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    state,
    actions,
    isReadOnly,
    isSavingGraph
  } = useBuilder();
  const {
    filter,
    setFilter,
    query,
    setQuery
  } = useBuilderNavigation();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getOrgAgentsToolsOptions();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const catalogQuery = useQuery(t1);
  const catalog = (catalogQuery.data?.tools ?? []).filter(isToolAvailable);
  const integrationsQuery = useQuery({
    ...getOrgVendorIntegrationsOptions(),
    enabled: catalog.some(_temp)
  });
  const integrations = integrationsQuery.data ?? [];
  const selectedKeys = enabledToolKeys(state.bundle.tools);
  const orphaned = findOrphanedToolMentions(state.bundle, catalog);
  let t2;
  if ($[2] !== actions || $[3] !== state.bundle.tools) {
    t2 = (catalogKind, enabled) => {
      const key = toolKeyForCatalogKind(catalogKind);
      const next = enabled ? [...state.bundle.tools, graphToolForCatalogKind(catalogKind)] : state.bundle.tools.filter(tool_0 => toolKey(tool_0) !== key);
      actions.setTools(next);
    };
    $[2] = actions;
    $[3] = state.bundle.tools;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const toggleTool = t2;
  let t3;
  if ($[5] !== state.bundle.tools) {
    t3 = catalogKind_0 => state.bundle.tools.find(tool_1 => tool_1.kind === "vendor" && toolKey(tool_1) === toolKeyForCatalogKind(catalogKind_0));
    $[5] = state.bundle.tools;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const vendorToolFor = t3;
  let t4;
  if ($[7] !== actions || $[8] !== state.bundle.tools) {
    t4 = (catalogKind_1, integrationId) => {
      const key_0 = toolKeyForCatalogKind(catalogKind_1);
      actions.setTools(state.bundle.tools.map(tool_2 => tool_2.kind === "vendor" && toolKey(tool_2) === key_0 ? {
        ...tool_2,
        data: {
          ...tool_2.data,
          integrationId
        }
      } : tool_2));
    };
    $[7] = actions;
    $[8] = state.bundle.tools;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const setIntegration = t4;
  const enabledCount = catalog.filter(tool_3 => selectedKeys.has(toolKeyForCatalogKind(tool_3.kind))).length;
  const active = activeToolFilters(catalog.length, filter, query);
  const needle = active.needle;
  const rows = catalog.map(tool_4 => ({
    tool: tool_4,
    enabled: selectedKeys.has(toolKeyForCatalogKind(tool_4.kind))
  })).sort(_temp2).filter(t5 => {
    const {
      enabled: enabled_0
    } = t5;
    return active.filter === "all" || enabled_0 === (active.filter === "enabled");
  }).filter(t6 => {
    const {
      tool: tool_5
    } = t6;
    return !needle || `${tool_5.displayName} ${toolSummary(tool_5.kind, tool_5.description)}`.toLowerCase().includes(needle);
  });
  const renderTools = () => {
    if (catalogQuery.isPending) {
      return <div role="status" aria-label={t(_temp3)} className="flex flex-col gap-1">{[0, 1, 2].map(_temp4)}</div>;
    }
    if (catalogQuery.error) {
      return <InlineAlert variant="error" cta={{
        label: t(_temp5),
        onClick: () => void catalogQuery.refetch()
      }}>{t(_temp6)}</InlineAlert>;
    }
    if (catalog.length === 0) {
      return <Card.Empty>{t(_temp7)}</Card.Empty>;
    }
    if (rows.length === 0) {
      const message = needle ? t(_temp8) : active.filter === "enabled" ? t(_temp9) : t(_temp10);
      return <Card.Empty role="status">{message}</Card.Empty>;
    }
    return <Card.List aria-label={t(_temp11)}>{rows.map(t7 => {
        const {
          tool: tool_6,
          enabled: enabled_1
        } = t7;
        const integrationOptions = integrationOptionsForKind(integrations, tool_6.kind);
        return <Card.Body as="li" key={tool_6.kind} className="flex items-center gap-4 px-4 py-3"><VendorLogo toolKind={tool_6.kind} name={tool_6.displayName} size="default" /><div className="flex min-w-0 flex-1 flex-col gap-1"><p className="text-label-2 text-primary">{tool_6.displayName}</p><p className="text-body-3 text-tertiary">{toolSummary(tool_6.kind, tool_6.description)}</p></div>{enabled_1 && integrationOptions.length > 1 && <Select size="tiny" className="w-44" aria-label={t(_temp12, {
            name: tool_6.displayName
          })} value={vendorToolFor(tool_6.kind)?.data.integrationId ?? integrationOptions[0].value} options={integrationOptions} disabled={isReadOnly || isSavingGraph} onChange={value => setIntegration(tool_6.kind, value)} />}<Toggle aria-label={tool_6.displayName} checked={enabled_1} disabled={isReadOnly || isSavingGraph} onChange={value_0 => toggleTool(tool_6.kind, value_0)} /></Card.Body>;
      })}</Card.List>;
  };
  const t8 = "max-w-[800px] mx-auto px-6 py-8 sm:px-10 flex flex-col gap-8";
  let t9;
  if ($[10] !== t) {
    t9 = t(_temp13);
    $[10] = t;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t9) {
    t10 = <h2 className="text-heading-2 text-primary">{t9}</h2>;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t) {
    t11 = t(_temp14);
    $[14] = t;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11) {
    t12 = <p className="text-body-2 text-tertiary">{t11}</p>;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t10 || $[19] !== t12) {
    t13 = <div className="flex flex-col gap-1">{t10}{t12}</div>;
    $[18] = t10;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = "@container flex flex-col gap-6";
  const t15 = orphaned.length > 0 && <InlineAlert variant="warning">{orphaned.length === 1 ? t(_temp15, {
      names: orphaned[0].displayName
    }) : t(_temp16, {
      names: orphaned.map(_temp17).join(", ")
    })}</InlineAlert>;
  const t16 = active.canFilter && <ToolFilters filter={filter} query={query} enabledCount={enabledCount} onFilterChange={setFilter} onQueryChange={setQuery} label={t(_temp18)} searchLabel={t(_temp19)} />;
  const t17 = Card;
  const t18 = renderTools();
  let t19;
  if ($[21] !== t17.Root || $[22] !== t18) {
    t19 = <t17.Root>{t18}</t17.Root>;
    $[21] = t17.Root;
    $[22] = t18;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== t15 || $[25] !== t16 || $[26] !== t19) {
    t20 = <section className={t14}>{t15}{t16}{t19}</section>;
    $[24] = t15;
    $[25] = t16;
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t13 || $[29] !== t20) {
    t21 = <div className={t8}>{t13}{t20}</div>;
    $[28] = t13;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  return t21;
};
export default ToolsView;
function _temp(tool) {
  return tool.toolKind === "vendor";
}
function _temp2(a, b) {
  return a.tool.displayName.localeCompare(b.tool.displayName);
}
function _temp3($) {
  return $.loading;
}
function _temp4(index) {
  return <Card.Body key={index} className="flex gap-4 px-4 py-3"><Shimmer className="size-8 shrink-0 rounded-md" /><div className="flex flex-1 flex-col gap-2"><Shimmer height={16} width="30%" /><Shimmer height={16} width="75%" /></div></Card.Body>;
}
function _temp5($_0) {
  return $_0.retry;
}
function _temp6($_1) {
  return $_1.error;
}
function _temp7($_2) {
  return $_2.emptyState;
}
function _temp8($_3) {
  return $_3.noMatches;
}
function _temp9($_4) {
  return $_4.noneEnabled;
}
function _temp10($_5) {
  return $_5.allEnabled;
}
function _temp11($_6) {
  return $_6.list;
}
function _temp12($_7) {
  return $_7.integration;
}
function _temp13($_8) {
  return $_8.title;
}
function _temp14($_9) {
  return $_9.subtitle;
}
function _temp15($_10) {
  return $_10.orphaned_one;
}
function _temp16($_11) {
  return $_11.orphaned_other;
}
function _temp17(tool_7) {
  return tool_7.displayName;
}
function _temp18($_12) {
  return $_12.filters.label;
}
function _temp19($_13) {
  return $_13.search;
}